import { useCallback, useMemo } from "react";
import type { SelectedCustomer } from "../types/ICampaign";
import { searchCustomers } from "../api/campaigns";

interface CustomerOption {
  value: number;
  label: string;
  email: string;
  customer: SelectedCustomer;
}

function toOption(c: SelectedCustomer): CustomerOption {
  return {
    value: c.id,
    label: `${c.name}${c.email ? ` (${c.email})` : ""}`,
    email: c.email,
    customer: c,
  };
}

export function useCustomerSearch(
  localCustomers: SelectedCustomer[],
  onSelect: (customers: SelectedCustomer[], ids: number[] | null) => void
) {
  const loadOptions = useCallback(
    (inputValue: string, callback: (options: CustomerOption[]) => void) => {
      if (inputValue.length < 2) {
        callback([]);
        return;
      }
      searchCustomers(inputValue)
        .then((results) => {
          callback(results.filter((c) => c.email).map(toOption));
        })
        .catch(() => callback([]));
    },
    []
  );

  const handleSelect = useCallback(
    (selected: readonly CustomerOption[]) => {
      const customers = selected.map((opt) => opt.customer);
      onSelect(
        customers,
        customers.length > 0 ? customers.map((c) => c.id) : null
      );
    },
    [onSelect]
  );

  const selectValue = useMemo(
    () => (Array.isArray(localCustomers) ? localCustomers : []).map(toOption),
    [localCustomers]
  );

  return { loadOptions, handleSelect, selectValue };
}

export type { CustomerOption };
